import React, { useEffect, useMemo, useState } from 'react';
import { cx } from '../shared/ui/utils';

const normalizeImages = (images = []) =>
  (Array.isArray(images) ? images : [])
    .map((image, index) => {
      if (!image) return null;
      if (typeof image === 'string') {
        return {
          src: image,
          alt: `Gallery image ${index + 1}`,
        };
      }
      return {
        src: image.image_url || image.url || image.src || '',
        alt: image.alt_text || image.alt || `Gallery image ${index + 1}`,
      };
    })
    .filter((image) => image?.src);

const GalleryArrow = ({ direction = 'next', onClick, dark = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'inline-flex h-10 w-10 items-center justify-center rounded-full border shadow-sm transition',
      dark
        ? 'border-white/20 bg-slate-950/70 text-white hover:bg-slate-950/90'
        : 'border-white/80 bg-white/85 text-slate-700 hover:bg-white'
    )}
    aria-label={direction === 'next' ? 'Next image' : 'Previous image'}
  >
    <i className={cx('bi text-base', direction === 'next' ? 'bi-chevron-right' : 'bi-chevron-left')} />
  </button>
);

const PackageImageCarousel = ({ images = [], title = '', className = '' }) => {
  const gallery = useMemo(() => normalizeImages(images), [images]);
  const fallback = gallery.length
    ? gallery
    : [{ src: 'https://picsum.photos/seed/homevilas-package-detail/1400/900', alt: title || 'Package image' }];
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (index > fallback.length - 1) setIndex(0);
  }, [fallback.length, index]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') setLightboxOpen(false);
      if (event.key === 'ArrowLeft') setIndex((prev) => (prev - 1 + fallback.length) % fallback.length);
      if (event.key === 'ArrowRight') setIndex((prev) => (prev + 1) % fallback.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fallback.length, lightboxOpen]);

  const goPrev = () => setIndex((prev) => (prev - 1 + fallback.length) % fallback.length);
  const goNext = () => setIndex((prev) => (prev + 1) % fallback.length);
  const current = fallback[index];

  return (
    <>
      <div className={cx('space-y-4', className)}>
        <div className="relative overflow-hidden rounded-[32px] border border-[#dfe4ee] bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="block w-full text-left"
          >
            <img
              src={current.src}
              alt={current.alt || title}
              className="h-[260px] w-full object-cover md:h-[420px]"
            />
          </button>
          {fallback.length > 1 ? (
            <>
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <GalleryArrow direction="prev" onClick={goPrev} />
              </div>
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <GalleryArrow direction="next" onClick={goNext} />
              </div>
            </>
          ) : null}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-slate-950/70 via-slate-950/15 to-transparent px-5 py-5">
            <div className="flex items-center gap-2">
              {fallback.map((_, dotIndex) => (
                <button
                  key={`gallery-dot-${dotIndex}`}
                  type="button"
                  onClick={() => setIndex(dotIndex)}
                  className={cx(
                    'h-2.5 w-2.5 rounded-full border transition',
                    dotIndex === index ? 'border-white bg-white' : 'border-white/40 bg-white/20'
                  )}
                  aria-label={`Go to image ${dotIndex + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="rounded-full border border-white/20 bg-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white backdrop-blur"
            >
              Open gallery
            </button>
          </div>
        </div>

        {fallback.length > 1 ? (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {fallback.map((image, thumbIndex) => (
              <button
                key={`${image.src}-${thumbIndex}`}
                type="button"
                onClick={() => setIndex(thumbIndex)}
                className={cx(
                  'h-20 w-24 shrink-0 overflow-hidden rounded-2xl border transition md:h-24 md:w-32',
                  thumbIndex === index
                    ? 'border-[#0f766e] ring-2 ring-[#0f766e]/15'
                    : 'border-[#dfe4ee] hover:border-[#9aa8c4]'
                )}
              >
                <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {lightboxOpen ? (
        <div className="fixed inset-0 z-[120] bg-slate-950/92 px-4 py-6 backdrop-blur-sm">
          <div className="mx-auto flex h-full max-w-6xl flex-col">
            <div className="flex items-center justify-between pb-4 text-white">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/50">Gallery</p>
                <h3 className="mt-1 text-lg font-semibold">{title || 'Package gallery'}</h3>
              </div>
              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white"
              >
                Close
              </button>
            </div>
            <div className="relative flex min-h-0 flex-1 items-center justify-center">
              {fallback.length > 1 ? (
                <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2 md:left-6">
                  <GalleryArrow direction="prev" onClick={goPrev} dark />
                </div>
              ) : null}
              <img
                src={current.src}
                alt={current.alt || title}
                className="max-h-full w-full rounded-[28px] object-contain"
              />
              {fallback.length > 1 ? (
                <div className="absolute right-2 top-1/2 z-10 -translate-y-1/2 md:right-6">
                  <GalleryArrow direction="next" onClick={goNext} dark />
                </div>
              ) : null}
            </div>
            {fallback.length > 1 ? (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {fallback.map((image, thumbIndex) => (
                  <button
                    key={`lightbox-${image.src}-${thumbIndex}`}
                    type="button"
                    onClick={() => setIndex(thumbIndex)}
                    className={cx(
                      'h-20 w-24 shrink-0 overflow-hidden rounded-2xl border transition md:h-24 md:w-32',
                      thumbIndex === index
                        ? 'border-[#f3c971] ring-2 ring-[#f3c971]/25'
                        : 'border-white/15 opacity-80 hover:opacity-100'
                    )}
                  >
                    <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PackageImageCarousel;
